import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Row, Col, Empty, Button, DatePicker, Space, Slider, Tag, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDevice } from '../contexts/DeviceContext';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { usePhotoModal } from '../hooks/usePhotoModal';
import GpsSection from '../components/dashboard/GpsSection';
import PhotoSection from '../components/dashboard/PhotoSection';
import AudioSection from '../components/dashboard/AudioSection';
import NotificationSection from '../components/dashboard/NotificationSection';
import PhotoModal from '../components/dashboard/PhotoModal';
import { secureLogger } from '../utils/secureLogger';
import type { DashboardProps } from '../types/dashboard';

const DashboardPage: React.FC<DashboardProps> = ({ 
  notifications = [], 
  onMarkAsRead, 
  onWebSocketMessage,
  isRealtime = false
}) => {
  const { selectedDevice } = useDevice();
  const { setPageHeader } = usePageHeader();
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const { data, audioLoading, selectedDate, setSelectedDate, fetchTodayData, loadAudioFile, handleWebSocketMessage, photoMeta, loadPhotoPage, photoLoading, realtimeApiCalls, testApiCall } = useDashboardData(isRealtime, selectedHour);
  
  // デバッグ用: realtimeApiCallsの値を監視
  useEffect(() => {
    console.log('🔍 DashboardPage realtimeApiCalls:', realtimeApiCalls);
  }, [realtimeApiCalls]);
  const { selectedPhoto, fullPhotoUrl, photoLoading: photoModalLoading, handlePhotoClick, handleClosePhoto } = usePhotoModal();
  
  // 最新の値を参照するためのref
  const handleWebSocketMessageRef = useRef(handleWebSocketMessage);
  const selectedDeviceRef = useRef(selectedDevice);
  const handleRealtimeModeTransitionRef = useRef<() => void>();
  const onWebSocketMessageRef = useRef(onWebSocketMessage);

  const getLocalToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getDateLabel = () => {
    const today = getLocalToday();
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    
    if (selectedDate === today) return '今日';
    if (selectedDate === yesterday) return '昨日';
    return '過去';
  };
  
  const isToday = selectedDate === getLocalToday();

  // リアルタイムモード遷移時の時間スライダー自動移動
  const handleRealtimeModeTransition = useCallback(() => {
    // 今日を表示中かつ時間範囲が指定されている場合のみ
    if (isToday && selectedHour !== null) {
      const currentHour = new Date().getHours();
      setSelectedHour(currentHour);
    }
  }, [isToday, selectedHour]);
  
  // refを更新
  useEffect(() => {
    handleWebSocketMessageRef.current = handleWebSocketMessage;
    selectedDeviceRef.current = selectedDevice;
    handleRealtimeModeTransitionRef.current = handleRealtimeModeTransition;
    onWebSocketMessageRef.current = onWebSocketMessage;
  });

  // WebSocket通知処理
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDashboardWebSocketMessage = useCallback((message: any) => {
    if (!message) return;
    
    secureLogger.debug(`📨 Dashboard received WS message: ${JSON.stringify({type: message.type})}`);
    
    // useDashboardDataのハンドラーを呼び出し
    handleWebSocketMessageRef.current(message);
    
    // リアルタイムモード遷移の検知
    if (message.type === 'device_connected' && message.device_id === selectedDeviceRef.current?.id) {
      handleRealtimeModeTransitionRef.current?.();
    }
  }, []); // intentionally empty to prevent WebSocket handler rebind

  useEffect(() => {
    if (onWebSocketMessage) {
      secureLogger.debug('🚀 DashboardPage registering WebSocket handler...');
      onWebSocketMessage(handleDashboardWebSocketMessage);
      secureLogger.debug('✅ DashboardPage registered WebSocket handler - queued messages should flush now');
    }
    return () => {
      secureLogger.debug('🔄 DashboardPage unregistering WebSocket handler');
      onWebSocketMessage?.(null);
    };
  }, [onWebSocketMessage, handleDashboardWebSocketMessage]);

  const displayDate = (() => {
    const [year, month, day] = selectedDate.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('ja-JP');
  })();

  const handlePrevDay = () => {
    const prevDay = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
    setSelectedDate(prevDay);
  };

  const handleNextDay = () => {
    const nextDay = dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD');
    setSelectedDate(nextDay);
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedDate(date.format('YYYY-MM-DD'));
    }
  };

  const getTimeRangeText = (hour: number | null) => {
    if (hour === null) return "全日";
    return `${hour}:00-${hour + 1}:00`;
  };

  const handleHourChange = (value: number | null) => {
    setSelectedHour(value);
  };

  const handleHourChangeComplete = (value: number | null) => {
    fetchTodayData();
  };

  const pageHeaderControls = useMemo(() => (
    <Space>
      <Button
        icon={<LeftOutlined />}
        onClick={handlePrevDay}
        size="small"
      />
      <DatePicker
        value={dayjs(selectedDate)}
        onChange={handleDateChange}
        format="YYYY-MM-DD"
        allowClear={false}
        size="small"
      />
      <Button
        icon={<RightOutlined />}
        onClick={handleNextDay}
        disabled={isToday}
        size="small"
      />
      <Button onClick={fetchTodayData} loading={data.loading} size="small">
        更新
      </Button>
    </Space>
  ), [data.loading, fetchTodayData, isToday, selectedDate]);

  useEffect(() => {
    setPageHeader({
      title: `${getDateLabel()}のアクティビティ (${displayDate})`,
      controls: pageHeaderControls,
    });
  }, [displayDate, pageHeaderControls, selectedDate, setPageHeader]);

  useEffect(() => {
    return () => setPageHeader(null);
  }, [setPageHeader]);

  if (!selectedDevice) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="デバイスを選択してください"
      />
    );
  }

  return (
    <div>
      {/* API呼び出し状況 */}
      <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#f0f8ff', borderRadius: 4, fontSize: '12px' }}>
        <span style={{ marginRight: 8, fontWeight: 500 }}>API呼び出し:</span>
        <span style={{ color: '#999', fontSize: '10px', marginRight: 8 }}>({realtimeApiCalls?.length || 0}件)</span>
        {realtimeApiCalls && realtimeApiCalls.length > 0 ? (
          realtimeApiCalls.slice(0, 5).map(call => (
            <Tooltip key={call.id} title={`${call.url} - ${call.timestamp.toLocaleTimeString()}`}>
              <Tag 
                color={call.status === 'loading' ? 'blue' : call.status === 'success' ? 'green' : 'red'}
                style={{ margin: '0 4px 4px 0', fontSize: '11px' }}
              >
                {call.type} {call.status === 'loading' ? '...' : call.status === 'success' ? '✓' : '✗'}
              </Tag>
            </Tooltip>
          ))
        ) : (
          <span style={{ color: '#666', fontSize: '11px' }}>リアルタイムAPI呼び出しはありません</span>
        )}
      </div>

      {/* 時間範囲選択 */}
      <div style={{ marginBottom: 16, padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ minWidth: 80, fontSize: '14px', color: '#666' }}>時間帯:</span>
          <div style={{ flex: 1, maxWidth: 400 }}>
            <Slider
              min={0}
              max={23}
              step={1}
              value={selectedHour}
              onChange={handleHourChange}
              onAfterChange={handleHourChangeComplete}
              marks={{
                0: '0:00',
                6: '6:00',
                12: '12:00',
                18: '18:00',
                23: '23:00'
              }}
              tooltip={{
                formatter: (value) => value !== undefined ? `${value}:00-${value + 1}:00` : '全日'
              }}
            />
          </div>
          <span style={{ minWidth: 100, fontSize: '14px', fontWeight: 500 }}>
            {getTimeRangeText(selectedHour)}
          </span>
          <Button 
            size="small" 
            onClick={() => {
              setSelectedHour(null);
              handleHourChangeComplete(null);
            }}
          >
            全日
          </Button>
        </div>
      </div>

      <Row gutter={16}>
        {/* 左列: 地図とGPS履歴 */}
        <Col span={12}>
          <GpsSection 
            gpsData={data.gps}
            photoData={data.photos}
            debugData={data.debugData}
          />
        </Col>

        {/* 右列: 写真・音声・通知 */}
        <Col span={12}>
          <PhotoSection 
            photoData={data.photos}
            debugData={data.debugData}
            onPhotoClick={handlePhotoClick}
            photoMeta={photoMeta}
            onLoadPhotoPage={loadPhotoPage}
            photoLoading={photoLoading}
          />

          <AudioSection 
            audioData={data.audio}
            transcriptData={data.transcripts}
            audioLoading={audioLoading}
            debugData={data.debugData}
            isRealtime={isRealtime}
            onLoadAudio={loadAudioFile}
            onRefreshAudio={isRealtime ? fetchTodayData : undefined}
          />

          <NotificationSection 
            notifications={notifications}
            onMarkAsRead={onMarkAsRead}
          />
        </Col>
      </Row>
      
      <PhotoModal 
        selectedPhoto={selectedPhoto}
        fullPhotoUrl={fullPhotoUrl}
        photoLoading={photoModalLoading}
        onClose={handleClosePhoto}
      />
    </div>
  );
};

export default DashboardPage;
